import { array_is_list, fromJson, phpCopy, is_numeric, PhpFloat, max, min } from '../../php-compat';
import type { PhpRef } from '../../port-contract';
import { wallIntersectsOblong, wallIntersectsSweep, type PaintedWallGeometry, type WallPoint } from '../../../shared/painted-wall-geometry';
import { array, at, f, isArray, list, native, num, record, values } from './php-shapes';

/** PHP: ft_painted_wall_geometry (includes/functions/janus/encounter/painted_wall_geometry.php:5) */
export function ftPaintedWallGeometry(input: unknown): unknown {
    let value = input;
    if (typeof value === 'string') {
        // PHP strlen counts UTF-8 bytes, not JS code units.
        if (encodeURIComponent(value).replace(/%[A-F\d]{2}/g, 'x').length > 150000) return null;
        try { value = fromJson(value, true); } catch { return null; }
    }
    const points = at(value, 'points');
    if (!isArray(value) || at(value, 'schema') !== 'ft.wall-path.v1' || !isArray(points) || !array_is_list(array(points))
        || array(points).size < 2 || array(points).size > 1024 || !['surface', 'floating'].includes(String(at(value, 'attachment')))) return null;
    const numeric = (v: unknown): boolean => (typeof v === 'number' || typeof v === 'bigint' || v instanceof PhpFloat) && Number.isFinite(num(v));
    for (const key of ['heightFt', 'thicknessFt']) { const v = at(value, key); if (!numeric(v) || num(v) <= 0 || num(v) > 10000) return null; }
    const out = [];
    for (const point of values(points)) {
        if (!isArray(point)) return null;
        for (const axis of ['x', 'y', 'z']) { const v = at(point, axis); if (!numeric(v) || Math.abs(num(v)) > 10000000) return null; }
        // array_intersect_key preserves the point's original member order.
        const p = array(point).copy(phpCopy, [...array(point).keys()].filter(k => k === 'x' || k === 'y' || k === 'z'));
        const previous = out[out.length - 1];
        if (previous && Math.hypot(num(at(previous, 'x')) - num(at(p, 'x')), num(at(previous, 'y')) - num(at(p, 'y'))) < .001) return null;
        out.push(p);
    }
    return record({ schema: 'ft.wall-path.v1', points: list(out), heightFt: phpCopy(at(value, 'heightFt')), thicknessFt: phpCopy(at(value, 'thicknessFt')), attachment: at(value, 'attachment') });
}
/** PHP: ft_painted_wall_length (includes/functions/janus/encounter/painted_wall_geometry.php:36) */
export function ftPaintedWallLength(wall: unknown): unknown {
    const ps = values(at(wall, 'points')); let length = 0.0;
    for (let i = 1; i < ps.length; i++) {
        const a = ps[i - 1], b = ps[i];
        length += Math.sqrt((num(at(a, 'x')) - num(at(b, 'x'))) ** 2 + (num(at(a, 'y')) - num(at(b, 'y'))) ** 2 + (num(at(a, 'z')) - num(at(b, 'z'))) ** 2);
    }
    return f(length);
}
/** PHP: ft_painted_wall_intersects_sphere (includes/functions/janus/encounter/painted_wall_geometry.php:59) */
export function ftPaintedWallIntersectsSphere(wall: unknown, center: unknown, radius: unknown, contact: PhpRef<unknown> = { value: null }): boolean {
    contact.value = null;
    const r = num(radius); if (!Number.isFinite(r) || r < 0) return false;
    const ps = values(at(wall, 'points'));
    for (let i = 1; i < ps.length; i++) {
        const a = ps[i - 1], b = ps[i];
        const distance = (t: number): number => {
            const x = num(at(a, 'x')) + (num(at(b, 'x')) - num(at(a, 'x'))) * t;
            const y = num(at(a, 'y')) + (num(at(b, 'y')) - num(at(a, 'y'))) * t;
            const z = num(at(a, 'z')) + (num(at(b, 'z')) - num(at(a, 'z'))) * t;
            const horizontal = Math.max(0, Math.hypot(num(at(center, 'x')) - x, num(at(center, 'y')) - y) - num(at(wall, 'thicknessFt')) / 2);
            const vertical = Math.max(0, z - num(at(center, 'z')), num(at(center, 'z')) - z - num(at(wall, 'heightFt')));
            return horizontal * horizontal + vertical * vertical;
        };
        let low = 0.0, high = 1.0;
        for (let j = 0; j < 64; j++) {
            const left = low + (high - low) / 3, right = high - (high - low) / 3;
            if (distance(left) < distance(right)) high = right; else low = left;
        }
        let t = (low + high) / 2;
        for (const edge of [0.0, 1.0]) if (distance(edge) < distance(t)) t = edge;
        if (distance(t) <= (r + .001) ** 2) {
            const x = num(at(a, 'x')) + (num(at(b, 'x')) - num(at(a, 'x'))) * t;
            const y = num(at(a, 'y')) + (num(at(b, 'y')) - num(at(a, 'y'))) * t;
            const z = num(at(a, 'z')) + (num(at(b, 'z')) - num(at(a, 'z'))) * t;
            const d = Math.hypot(num(at(center, 'x')) - x, num(at(center, 'y')) - y);
            const ratio = d > 0 ? Math.min(1, num(at(wall, 'thicknessFt')) / 2 / d) : 0;
            contact.value = record({ x: f(x + (num(at(center, 'x')) - x) * ratio), y: f(y + (num(at(center, 'y')) - y) * ratio),
                z: max(f(z), min(f(z + num(at(wall, 'heightFt'))), at(center, 'z'))) });
            return true;
        }
    }
    return false;
}
/** PHP: ft_painted_wall_intersects_cylinder (includes/functions/janus/encounter/painted_wall_geometry.php:89) */
export function ftPaintedWallIntersectsCylinder(wall: unknown, point: unknown, radius: unknown, height: unknown): boolean {
    const ps = values(at(wall, 'points'));
    for (let i = 1; i < ps.length; i++) {
        const a = ps[i - 1], b = ps[i], dx = num(at(b, 'x')) - num(at(a, 'x')), dy = num(at(b, 'y')) - num(at(a, 'y')), dz = num(at(b, 'z')) - num(at(a, 'z'));
        let low = 0.0, high = 1.0;
        const minBase = num(at(point, 'z')) - num(at(wall, 'heightFt')) - .001, maxBase = num(at(point, 'z')) + num(height) + .001;
        if (Math.abs(dz) < 1e-9) { if (num(at(a, 'z')) < minBase || num(at(a, 'z')) > maxBase) continue; }
        else { const t1 = (minBase - num(at(a, 'z'))) / dz, t2 = (maxBase - num(at(a, 'z'))) / dz;
            low = Math.max(0, Math.min(t1, t2)); high = Math.min(1, Math.max(t1, t2)); if (low > high) continue; }
        const divisor = dx * dx + dy * dy;
        if (divisor === 0) { const error = new Error('Division by zero'); error.name = 'DivisionByZeroError'; throw error; }
        const t = Math.max(low, Math.min(high, ((num(at(point, 'x')) - num(at(a, 'x'))) * dx + (num(at(point, 'y')) - num(at(a, 'y'))) * dy) / divisor));
        if (Math.hypot(num(at(point, 'x')) - num(at(a, 'x')) - t * dx, num(at(point, 'y')) - num(at(a, 'y')) - t * dy) <= num(radius) + num(at(wall, 'thicknessFt')) / 2 + .001) return true;
    }
    return false;
}
/** PHP: ft_painted_wall_intersects_oblong (includes/functions/janus/encounter/painted_wall_geometry.php:110) */
export function ftPaintedWallIntersectsOblong(wall: unknown, point: unknown, halfW: unknown, halfD: unknown, facing: unknown, height: unknown): boolean {
    return wallIntersectsOblong(native(wall) as PaintedWallGeometry, native(point) as WallPoint, num(halfW), num(halfD), num(facing), num(height));
}
/** PHP: ft_painted_wall_intersects_sweep (includes/functions/janus/encounter/painted_wall_geometry.php:145) */
export function ftPaintedWallIntersectsSweep(wall: unknown, from: unknown, to: unknown, radius: unknown, height: unknown): boolean {
    return wallIntersectsSweep(native(wall) as PaintedWallGeometry, native(from) as WallPoint, native(to) as WallPoint, num(radius), num(height));
}

/** PHP: ft_painted_wall_samples (includes/functions/janus/encounter/painted_wall_geometry.php:46) */
export function ftPaintedWallSamples(wall: unknown): Generator<unknown> {
    const captured = phpCopy(wall);
    function* samples(): Generator<unknown> {
        const points = values(at(captured, 'points')); yield phpCopy(points[0]);
        for (let i = 1; i < points.length; i++) {
            const a = points[i - 1], b = points[i];
            const dx = num(at(b, 'x')) - num(at(a, 'x')), dy = num(at(b, 'y')) - num(at(a, 'y')), dz = num(at(b, 'z')) - num(at(a, 'z'));
            const steps = Math.max(1, Math.ceil(Math.sqrt(dx ** 2 + dy ** 2 + dz ** 2) / .25));
            for (let j = 1; j <= steps; j++) {
                const t = j / steps;
                // PHP integer division stays integral at the final sample;
                // integer endpoint arithmetic then stays integral too.
                yield record(Object.fromEntries(['x','y','z'].map(axis => {
                    const av = at(a, axis), bv = at(b, axis), value = num(av) + (num(bv) - num(av)) * t;
                    return [axis, j === steps && typeof av === 'number' && Number.isInteger(av) && typeof bv === 'number' && Number.isInteger(bv) && Number.isSafeInteger(value) ? value : f(value)];
                })));
            }
        }
    }
    return samples();
}
